import { useEffect, useRef, useState } from 'react';
import { CountUp } from 'countup.js';
import type { ImpactResult, RecommendedPlanSet } from '../types';
import { formatAvv, pct, applyUplift } from '../format';
import { Chart } from './Chart';
import { buildSignUpUrl } from '../analytics';
import { NarrativeCard } from './NarrativeCard';
import { Comparable } from './Comparable';
import { LickdLogo } from './LickdLogo';
import { PLAN_API_BASE_URL, RECOMMENDED_PLAN_MAX_AVV } from '../constants';
import { fetchRecommendedPlan } from '../api';
import { RecommendedPlan } from './RecommendedPlan';
import { ArrowRight } from './ArrowRight';

/**
 * The full results view. Pure presentation - every number comes from the API's
 * ImpactResult; the only client-side maths is applying the returned uplift
 * percentages to the creator's own averages for display.
 */
interface Props {
  result: ImpactResult;
  signupRef?: string;
  onBack: () => void;
}

export function Results({ result, signupRef, onBack }: Props) {
  const isInitial = useRef(true);
  const [recommendedPlanSet, setRecommendedPlanSet] = useState<RecommendedPlanSet | null>(null);
  const countupRef = useRef(null);
  const { channel, projection, comparables, narrative, footnotes, videos, existingLickdUser } = result;
  const { medianViews, p25, p75, medianEng, insufficient } = projection;
  const { uploadsPerMonth, avgViews, avgEng } = channel;

  const projViews = applyUplift(avgViews, medianViews);
  const projEng = applyUplift(avgEng, medianEng);

  const positiveUpliftComparables = comparables.filter((comp) => comp.lickdUpliftViews > 0);

  useEffect(() => {
    if (insufficient || !countupRef.current) return;

    const countUpAnim = new CountUp(countupRef.current, medianViews, { prefix: '+', suffix: '%' });
    if (!countUpAnim.error) {
      countUpAnim.start();
    }

    return () => {
      countUpAnim.onDestroy();
    };
  }, [insufficient, medianViews]);

  useEffect(() => {
    // Run once per mount, and only if the AVV is < 5m
    if (!isInitial.current || avgViews > RECOMMENDED_PLAN_MAX_AVV || existingLickdUser) return;
    isInitial.current = false;

    fetchRecommendedPlan(PLAN_API_BASE_URL, { avgViews, uploadsPerMonth })
      .then((res) => {
        // The currency toggle offers all three, so a partial set would crash on selection
        if (res?.USD && res.GBP && res.EUR) {
          setRecommendedPlanSet(res);
        }
      })
      .catch(() => {
        // Swallow any request errors, as the recommended plan is non-critical
      });
  }, [avgViews, uploadsPerMonth, existingLickdUser]);

  const signupUrl = buildSignUpUrl(signupRef);

  return (
    <div className="results">
      <button className="back" onClick={onBack}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Search another channel
      </button>

      {/* Channel card */}
      <div className="channel-card">
        {channel.thumbnail ? (
          <img className="channel-image" src={channel.thumbnail} alt="" />
        ) : (
          <div className="channel-image" />
        )}

        <div className="channel-info">
          <h4 className="channel-name">{channel.title}</h4>
          <div className="channel-meta">
            {channel.handle} | {channel.avv} average video views
          </div>
          <div className="channel-badges">
            <span className="badge badge-green">{channel.vertical}</span>
          </div>
        </div>
      </div>

      {existingLickdUser ? (
        <div className="results-container">
          <p className="text-center">Due to privacy settings we can't show uplift statistics for this channel</p>
        </div>
      ) : (
        <div className="results-container">
          {/* Headline */}
          <div className={`headline${insufficient ? ' insufficient' : ''} text-center`}>
            {insufficient ? (
              <div className="hn">Creators like you typically see a meaningful increase in views with Lickd</div>
            ) : (
              <>
                <div className="hn">
                  <span className="uplift-pct" ref={countupRef}>
                    {pct(medianViews)}
                  </span>
                  more views with Lickd
                </div>
                <div className="hs">
                  On average, creators like you see {pct(p25)} to {pct(p75)} more views
                </div>
              </>
            )}
          </div>

          {/* Narrative */}
          {!insufficient && narrative.length > 0 && (
            <ul className="narrative-cards">
              {narrative.map((narrativeCard, i) => (
                <NarrativeCard key={i} narrative={narrativeCard} />
              ))}
            </ul>
          )}

          {/* Footnotes */}
          {footnotes.length > 0 && (
            <div className="footnotes">
              <h3>Tips to get the most from Lickd</h3>

              <ul className="footnotes-list">
                {footnotes.map((footnote, i) => (
                  <li key={i}>{footnote}</li>
                ))}
              </ul>
            </div>
          )}

          {!insufficient && (
            <div className="cta text-center">
              <a className="btn btn-primary cta-btn" href={signupUrl} target="_blank" rel="noopener noreferrer">
                Sign up
                <ArrowRight />
              </a>
            </div>
          )}

          {/* Projections */}
          {!insufficient && (
            <div className="stats">
              <h3>See what your channel looks like with Lickd</h3>

              <div className="projections">
                <table className="projections-table">
                  <thead>
                    <tr>
                      <th scope="col" aria-label="Metric"></th>
                      <th scope="col" className="projections-actual">
                        Current
                      </th>
                      <th scope="col" className="projections-lickd" aria-label="With Lickd">
                        <LickdLogo size={46} />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <th scope="row">Average video views</th>
                      <td className="projections-actual">{formatAvv(avgViews)}</td>
                      <td className="projections-lickd">{formatAvv(projViews)}</td>
                    </tr>
                    <tr>
                      <th scope="row">Average engagement</th>
                      <td className="projections-actual">{avgEng}%</td>
                      <td className="projections-lickd">{projEng.toFixed(2)}%</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Chart */}
              {videos.length > 0 && (
                <div className="chart-card">
                  <div className="chart-head">
                    <h4 className="chart-title">Your last 10 videos</h4>
                    <div className="legend">
                      <div className="legend-item">
                        <span className="legend-dot legend-dot-actual" />
                        Actual views
                      </div>
                      <div className="legend-item">
                        <span className="legend-dot legend-dot-lickd" />
                        With Lickd
                      </div>
                    </div>
                  </div>
                  <div className="chart-body">
                    <Chart videos={videos} medianUplift={medianViews} />
                    <div className="chart-labels">
                      <div className="chart-label">Oldest</div>
                      <div className="chart-label chart-label-end">Newest</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Comparables */}
          {!insufficient && positiveUpliftComparables.length > 0 && (
            <div className="comps">
              <h3>How we measure: creators like you already using Lickd</h3>

              <div className="comps-list">
                {positiveUpliftComparables.map((comparable, index) => (
                  <Comparable
                    key={comparable.id}
                    scoredComparable={comparable}
                    className={index === positiveUpliftComparables.length - 1 ? 'comp-card--final' : undefined}
                  />
                ))}
              </div>
            </div>
          )}

          {!insufficient && recommendedPlanSet && (
            <RecommendedPlan planSet={recommendedPlanSet} signupUrl={signupUrl} />
          )}

          <div className="disclaimer text-center">
            Projections are based on real users' YouTube data and reflect average growth. Many creators see a mainstream
            music effect, though individual results aren't guaranteed. AVV displayed is illustrative, final AVV
            calculations during sign-up may differ.
          </div>
        </div>
      )}
    </div>
  );
}
